import React, { useState, useEffect } from "react";
import adminApi from "../adminApi";
import { useLanguage } from "../context/LanguageContext";
import "./UserManagement.css";

interface AdminUser {
  id: number;
  username: string;
  role: string;
  is_active: number;
  created_at: string;
}

const UserManagement: React.FC = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("admin");
  
  const [activeTab, setActiveTab] = useState<"users" | "roles">("users");

  const fetchUsers = async () => {
    try {
      const response = await adminApi.get("/users");
      setUsers(response.data);
    } catch (err) {
      setError(t("Nie udało się pobrać użytkowników"));
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await adminApi.get("/roles");
      setRoles(response.data);
    } catch (err) {
      setError(t("Nie udało się pobrać ról"));
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchRoles()]);
      setLoading(false);
    };
    init();
  }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername || !newPassword) return;
    try {
      await adminApi.post("/users", { 
        username: newUsername, 
        password: newPassword,
        role: newRole 
      });
      setNewUsername("");
      setNewPassword("");
      setNewRole("admin");
      setShowAddForm(false);
      fetchUsers();
    } catch (err) {
      setError(t("Nie udało się dodać użytkownika"));
    }
  };

  const handleDeleteUser = async (id: number, username: string) => {
    if (username === "admin") {
      alert(t("Nie można usunąć głównego konta admina"));
      return;
    }
    if (window.confirm(`${t("Czy na pewno usunąć użytkownika")} ${username}?`)) {
      try {
        await adminApi.delete(`/users/${id}`);
        fetchUsers();
      } catch (err) {
        setError(t("Nie udało się usunąć użytkownika"));
      }
    }
  };

  const handleTogglePermission = async (roleId: number, roleName: string, permissions: string[], permission: string) => {
    let newPermissions: string[];
    if (permissions.includes(permission)) {
      newPermissions = permissions.filter(p => p !== permission);
    } else {
      newPermissions = [...permissions, permission];
    }

    try {
      await adminApi.put(`/roles/${roleId}`, { permissions: newPermissions });
      fetchRoles();
    } catch (err) {
      setError(t("Nie udało się zaktualizować uprawnień"));
    }
  };

  const allPossiblePermissions: string[] = [
    "Lock types", "Lock properties", "Supplier Icon", "Database preview", "Backups", "Tax change", "Database update", "Users"
  ];

  if (loading) return <p>{t("Ładowanie...")}</p>;

  return (
    <div className="admin-container">
      <div className="view-tabs" style={{ marginBottom: '20px', borderBottom: '1px solid #ddd' }}>
        <button 
          onClick={() => setActiveTab("users")}
          style={{ padding: '10px 20px', background: activeTab === 'users' ? '#f0f0f0' : 'none', border: 'none', cursor: 'pointer' }}
        >
          {t("Użytkownicy")}
        </button>
        <button 
          onClick={() => setActiveTab("roles")}
          style={{ padding: '10px 20px', background: activeTab === 'roles' ? '#f0f0f0' : 'none', border: 'none', cursor: 'pointer' }}
        >
          {t("Role i Uprawnienia")}
        </button>
      </div>

      {activeTab === "users" ? (
        <>
          <div className="admin-header">
            <h2>{t("Zarządzanie Użytkownikami")}</h2>
            <button onClick={() => setShowAddForm(!showAddForm)}>
              {showAddForm ? t("Anuluj") : t("Dodaj użytkownika")}
            </button>
          </div>

          {showAddForm && (
            <form className="add-user-form" onSubmit={handleAddUser}>
              <input
                type="text"
                placeholder={t("Login")}
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
              />
              <input
                type="password"
                placeholder={t("Hasło")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <select value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                {roles.map(r => (
                  <option key={r.id} value={r.role_name}>{r.role_name}</option>
                ))}
              </select>
              <button type="submit">{t("Zapisz")}</button>
            </form>
          )}

          {error && <p className="error-message">{error}</p>}

          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t("Użytkownik")}</th>
                <th>{t("Rola")}</th>
                <th>{t("Data utworzenia")}</th>
                <th>{t("Akcje")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.username}</td>
                  <td>{user.role}</td>
                  <td>{new Date(user.created_at).toLocaleString()}</td>
                  <td>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteUser(user.id, user.username)}
                      disabled={user.username === "admin"}
                    >
                      {t("Usuń")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <>
          <div className="admin-header">
            <h2>{t("Role i Uprawnienia")}</h2>
          </div>
          {error && <p className="error-message">{error}</p>}
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t("Rola")}</th>
                <th>{t("Uprawnienia")}</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td style={{ fontWeight: 'bold' }}>{role.role_name}</td>
                  <td>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                      {allPossiblePermissions.map(perm => (
                        <label key={perm} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <input 
                            type="checkbox" 
                            checked={role.permissions.includes(perm)}
                            onChange={() => handleTogglePermission(role.id, role.role_name, role.permissions, perm)}
                            disabled={role.role_name === 'admin'}
                          />
                          {t(perm)}
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default UserManagement;
